import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  MenuItem,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@offline-mui/material'
import { useEffect, useState } from 'react'
import { accountingRequest, formatMoney, jsonInit } from '../api'
import CardPaymentDialog from './CardPaymentDialog'

// SND-14228: invoice search modelled on Gestion's "Invoice selection". Every filled field must match; an office on
// its own lists that office's invoices. PAX name and Ticket no match anywhere in the value. The form sits in the Leo header, the results in a card below it.
const EMPTY_CRITERIA = {
  office: '',
  invoiceNumber: '',
  recordLocator: '',
  paxName: '',
  ticketNumber: '',
  checkNumber: '',
  cardFirst6: '',
  cardEnding: '',
}

type Criteria = typeof EMPTY_CRITERIA

export type SearchOutcome = { results: any[]; truncated: boolean }

// The last search (form and results), kept in memory for this tab so coming back from an invoice shows it again.
// Not in browser storage: card digits stay off disk, and a reload starts with an empty search.
let lastSearch: { criteria: Criteria; outcome: SearchOutcome } | null = null

export function lastSearchOutcome() {
  return lastSearch ? lastSearch.outcome : null
}

function validate(criteria: Criteria) {
  const invoiceNumber = criteria.invoiceNumber.trim()
  const recordLocator = criteria.recordLocator.trim()
  if (invoiceNumber && invoiceNumber.length < 2) return 'Invoice # needs at least 2 characters.'
  if (recordLocator && recordLocator.length < 3) return 'PNR locator needs at least 3 characters.'
  if ((criteria.cardFirst6 || criteria.cardEnding) && !/^\d{6}$/.test(criteria.cardFirst6)) {
    return 'Credit card no needs the 6 starting digits.'
  }
  const filled = [
    criteria.office,
    invoiceNumber,
    recordLocator,
    criteria.paxName.trim(),
    criteria.ticketNumber.trim(),
    criteria.checkNumber.trim(),
    criteria.cardFirst6,
  ].some(Boolean)
  if (!filled) return 'Choose an office or enter at least one of Invoice #, PNR locator, PAX name, Ticket no, Check no or Credit card no.'
  return ''
}

// White fields on the header gradient.
const fieldSx = {
  bgcolor: 'common.white',
  borderRadius: 1,
  '& .MuiOutlinedInput-notchedOutline': { borderColor: 'transparent' },
}

function Row({ label, children }: { label: string; children: any }) {
  return (
    <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '110px 1fr' }, alignItems: 'center', gap: 1.5 }}>
      <Typography variant="body2" sx={{ fontWeight: 600, color: 'common.white' }}>{label}</Typography>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, flexWrap: 'wrap' }}>{children}</Box>
    </Box>
  )
}

function Hint({ children }: { children: any }) {
  return <Typography variant="caption" sx={{ color: '#d6e4f5' }}>{children}</Typography>
}

export function InvoiceSearchForm({
  onResults,
  onClear,
}: {
  onResults: (outcome: SearchOutcome) => void
  onClear: () => void
}) {
  const [criteria, setCriteria] = useState<Criteria>({ ...(lastSearch ? lastSearch.criteria : EMPTY_CRITERIA) })
  const [offices, setOffices] = useState<any[]>([])
  const [searching, setSearching] = useState(false)
  const [error, setError] = useState('')

  useEffect(() => {
    accountingRequest('/offices')
      .then((body) => setOffices(body.offices || []))
      .catch(() => setOffices([]))
  }, [])

  function setField(key: keyof Criteria, value: string) {
    setCriteria((current) => ({ ...current, [key]: value }))
  }

  async function search(event: any) {
    event.preventDefault()
    const problem = validate(criteria)
    if (problem) {
      setError(problem)
      return
    }
    setSearching(true)
    setError('')
    try {
      const body = await accountingRequest('/invoices/search', jsonInit('POST', criteria))
      const outcome = { results: body.results || [], truncated: Boolean(body.truncated) }
      lastSearch = { criteria: { ...criteria }, outcome }
      onResults(outcome)
    } catch (err: any) {
      setError(err.message)
    } finally {
      setSearching(false)
    }
  }

  function clear() {
    lastSearch = null
    setCriteria({ ...EMPTY_CRITERIA })
    setError('')
    onClear()
  }

  const inputSx = { ...fieldSx, width: 190 }

  return (
    <Box component="form" onSubmit={search} aria-label="Search invoice">
      <Typography variant="subtitle1" sx={{ color: '#ffe8c2', fontWeight: 700, mb: 1.5 }}>Search Invoice</Typography>
      <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' }, columnGap: 4, rowGap: 1.25 }}>
        <Box sx={{ display: 'grid', gap: 1.25, alignContent: 'start' }}>
          <Row label="Office">
            <TextField
              select
              size="small"
              value={criteria.office}
              onChange={(event) => setField('office', event.target.value)}
              slotProps={{ select: { displayEmpty: true }, htmlInput: { 'aria-label': 'Office' } }}
              sx={{ ...fieldSx, width: 250 }}
            >
              <MenuItem value="">All offices</MenuItem>
              {offices.map((office) => (
                <MenuItem key={office.code} value={office.code}>{office.name || office.code}</MenuItem>
              ))}
            </TextField>
          </Row>
          <Row label="Invoice #">
            <TextField
              size="small"
              value={criteria.invoiceNumber}
              onChange={(event) => setField('invoiceNumber', event.target.value.toUpperCase().slice(0, 6))}
              slotProps={{ htmlInput: { 'aria-label': 'Invoice number', maxLength: 6 } }}
              sx={inputSx}
            />
            <Hint>(at least 2 characters)</Hint>
          </Row>
          <Row label="PNR locator">
            <TextField
              size="small"
              value={criteria.recordLocator}
              onChange={(event) => setField('recordLocator', event.target.value.toUpperCase().slice(0, 6))}
              slotProps={{ htmlInput: { 'aria-label': 'PNR locator', maxLength: 6 } }}
              sx={inputSx}
            />
            <Hint>(at least 3 characters)</Hint>
          </Row>
          <Row label="Check no">
            <TextField
              size="small"
              value={criteria.checkNumber}
              onChange={(event) => setField('checkNumber', event.target.value.replace(/[^A-Za-z0-9-]/g, '').slice(0, 20))}
              slotProps={{ htmlInput: { 'aria-label': 'Check number', maxLength: 20 } }}
              sx={inputSx}
            />
          </Row>
        </Box>
        <Box sx={{ display: 'grid', gap: 1.25, alignContent: 'start' }}>
          <Row label="PAX name">
            <TextField
              size="small"
              value={criteria.paxName}
              onChange={(event) => setField('paxName', event.target.value)}
              slotProps={{ htmlInput: { 'aria-label': 'Passenger name', maxLength: 60 } }}
              sx={inputSx}
            />
          </Row>
          <Row label="Ticket no">
            <TextField
              size="small"
              value={criteria.ticketNumber}
              onChange={(event) => setField('ticketNumber', event.target.value.replace(/[^\d\s-]/g, '').slice(0, 20))}
              slotProps={{ htmlInput: { 'aria-label': 'Ticket number', inputMode: 'numeric', maxLength: 20 } }}
              sx={inputSx}
            />
          </Row>
          <Row label="Credit card no">
            <TextField
              size="small"
              placeholder="6 starting digits"
              value={criteria.cardFirst6}
              onChange={(event) => setField('cardFirst6', event.target.value.replace(/\D/g, '').slice(0, 6))}
              slotProps={{ htmlInput: { 'aria-label': 'Card 6 starting digits', inputMode: 'numeric', maxLength: 6, autoComplete: 'off' } }}
              sx={{ ...fieldSx, width: 150 }}
            />
            <TextField
              size="small"
              placeholder="Ending (optional)"
              value={criteria.cardEnding}
              onChange={(event) => setField('cardEnding', event.target.value.replace(/\D/g, '').slice(0, 4))}
              slotProps={{ htmlInput: { 'aria-label': 'Card ending', inputMode: 'numeric', maxLength: 4, autoComplete: 'off' } }}
              sx={{ ...fieldSx, width: 150 }}
            />
          </Row>
        </Box>
      </Box>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 1.75 }}>
        <Button type="submit" variant="contained" color="warning" disabled={searching}>
          {searching ? 'Searching…' : 'Search'}
        </Button>
        <Button type="button" onClick={clear} disabled={searching} sx={{ color: 'common.white' }}>Clear</Button>
      </Box>
      {error ? <Alert severity="error" sx={{ mt: 1.5 }}>{error}</Alert> : null}
    </Box>
  )
}

export function InvoiceSearchResults({
  outcome,
  onOpenInvoice,
}: {
  outcome: SearchOutcome
  onOpenInvoice: (row: any) => void
}) {
  const { results, truncated } = outcome
  // SND-14275: Process Payment straight from a result (ticketing agents), on the invoice loaded for it.
  const [charging, setCharging] = useState<any>(null)
  const [loadingPnr, setLoadingPnr] = useState('')
  const [chargeError, setChargeError] = useState('')

  async function processPayment(row: any) {
    setChargeError('')
    setLoadingPnr(row.recordLocator)
    try {
      setCharging(await accountingRequest(`/invoices/${encodeURIComponent(row.recordLocator)}`))
    } catch (err: any) {
      setChargeError(err.message)
    } finally {
      setLoadingPnr('')
    }
  }

  return (
    <Card variant="outlined" sx={{ mt: 3 }}>
      <CardContent>
        <Typography variant="overline" color="info.main" sx={{ fontWeight: 700 }}>Search results</Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
          {results.length === 0
            ? 'No invoice matches.'
            : `${results.length} invoice${results.length === 1 ? '' : 's'}${truncated ? ' (first results only; narrow the search)' : ''}.`}
        </Typography>
        {chargeError ? <Alert severity="error" sx={{ mb: 1 }}>{chargeError}</Alert> : null}
        {results.length ? (
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow>
                  {['Invoice #', 'PNR', 'Office', 'Agency', 'Passengers', 'Booked', 'Departure', 'Status', 'Total', ''].map((header) => (
                    <TableCell key={header} align={header === 'Total' ? 'right' : 'left'}>{header}</TableCell>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>
                {results.map((row) => (
                  <TableRow
                    key={row.recordLocator}
                    hover
                    tabIndex={0}
                    onClick={() => onOpenInvoice(row)}
                    onKeyDown={(event) => {
                      if (event.key === 'Enter') onOpenInvoice(row)
                    }}
                    sx={{ cursor: 'pointer' }}
                  >
                    <TableCell sx={{ fontWeight: 700 }}>{row.invoiceNumber || '—'}</TableCell>
                    <TableCell>{row.shownRecordLocator || row.recordLocator}</TableCell>
                    <TableCell>{row.officeCode || '—'}</TableCell>
                    <TableCell>{row.agencyName || '—'}</TableCell>
                    <TableCell sx={{ maxWidth: 260 }}>{row.passengers || '—'}</TableCell>
                    <TableCell sx={{ whiteSpace: 'nowrap' }}>{row.bookingDate || '—'}</TableCell>
                    <TableCell sx={{ whiteSpace: 'nowrap' }}>{row.departureDate || '—'}</TableCell>
                    <TableCell sx={{ textTransform: 'capitalize' }}>{row.status || '—'}</TableCell>
                    <TableCell align="right" sx={{ whiteSpace: 'nowrap' }}>{formatMoney(row.total)}</TableCell>
                    <TableCell align="right" onClick={(event) => event.stopPropagation()} onKeyDown={(event) => event.stopPropagation()}>
                      <Button size="small" variant="outlined" color="secondary" disabled={loadingPnr === row.recordLocator} onClick={() => processPayment(row)} sx={{ whiteSpace: 'nowrap' }}>
                        {loadingPnr === row.recordLocator ? 'Opening…' : 'Process Payment'}
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        ) : null}
        <CardPaymentDialog
          open={Boolean(charging)}
          mode="charge"
          invoice={charging}
          onClose={() => setCharging(null)}
          onSaved={(invoice) => {
            setCharging(null)
            onOpenInvoice({ invoiceNumber: invoice.invoiceNumber, recordLocator: invoice.recordLocator })
          }}
        />
      </CardContent>
    </Card>
  )
}
